import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import moment from 'moment';
import { IDatepickerConfig, IDatePeriod, IPeriod, IDatepickerValue, IWeekPeriod } from '../types';
import { XUX_DATEPICKER_PERIODS } from '../datepicker-specific-constants';
import { BsLocaleService } from 'ngx-bootstrap/datepicker';
import { updateLocale, LocaleData, defineLocale } from 'ngx-bootstrap/chronos';
import { ComponentStaticText } from '../components';
import { get, isNil } from 'lodash';
import * as locales from 'ngx-bootstrap/locale';

const defaultConfig: IDatepickerConfig = {
  placeholder: '',
  required: true,
  showInputLabel: true,
  availableOptions: [XUX_DATEPICKER_PERIODS.SINGLE],
  yearsRange: [moment().subtract('6', 'year').get('year'), moment().get('year')],
  ignoreBackdropClick: false,
  dateFormat: {
    short_date_format: 'MM/DD/YYYY',
    long_date_format: 'MMM DD YYYY',
  },
};

@Component({
  selector: 'xux-datepicker-input',
  styleUrls: ['./xux-datepicker-input.component.scss'],
  templateUrl: './xux-datepicker-input.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class XuxDatepickerInputComponent {
  @Input('config') set _config(value: IDatepickerConfig) {
    const config = Object.assign({}, defaultConfig, value);
    const configMapper = get(value, 'configMapper');
    this.config = configMapper ? configMapper(config) : config;
    if (this.config.locale) {
      this.config.locale = this.config.locale.split('-')[0];
      this.setLocale(this.config.locale);
    }
  }
  @Input() public periods: IPeriod[];
  @Input() public weekPeriods?: IWeekPeriod[];
  @Input('value') set _defaultValue(data: IDatepickerValue) {
    if (data && data.value) {
      this.defaultValue = data;
      const { value: { dateStart, dateEnd } } = data;

      const isHandleWithMinDate = (date: string | Date) =>
        !isNil(get(this.config, 'minDate', null)) && moment(date).isBefore(moment(this.config.minDate));

      if (isHandleWithMinDate(dateStart)) {
        this.defaultValue.value.dateStart = new Date(this.config.minDate);
      }

      if (isHandleWithMinDate(dateEnd)) {
        this.defaultValue.value.dateEnd = null;
      }

      if (this.config && this.config.autoCorrectEndDate && dateStart > dateEnd) {
        this.defaultValue.value.dateEnd = new Date(dateStart);
      }

      this.applyValue(this.defaultValue, false);
    }
  }
  @Input() public set i18n(staticTextContent: ComponentStaticText) {
    this.staticTextContent = {
      ...this.staticTextContent,
      ...(staticTextContent || {}),
    };
  }

  @Output()
  public onChange: EventEmitter<IDatepickerValue> = new EventEmitter();

  public staticTextContent: ComponentStaticText = {
    period: 'Period',
    previousPeriod: 'Previous Period',
    currentPeriod: 'Current Period',
  };
  public config: IDatepickerConfig = defaultConfig;
  public show2ndField: boolean = false;
  public value: IDatePeriod;
  public label: string = '';
  public defaultValue: IDatepickerValue;

  protected modalRef: BsModalRef;

  constructor(private modalService: BsModalService, public bsLocaleService: BsLocaleService) {}

  public showModal(template: any, event: Event): void {
    if (this.config.disabled) {
      return;
    }
    this.modalRef = this.modalService.show(template, {
      class: 'modal-picker', ignoreBackdropClick: this.config.ignoreBackdropClick,
    });
    event.preventDefault(); // prevent datepicker input from being focused
  }

  public hideModal(): void {
    if (this.modalRef) {
      this.modalRef.hide();
    }
  }

  public applyValue(newData: IDatepickerValue, emitValue: boolean = true): void {
    this.value = newData.value;
    this.defaultValue = newData;

    if (this.value && !this.value.dateEnd) {
      this.show2ndField = false;
    }

    if (this.value && this.value.dateEnd) {
      this.show2ndField = this.config && this.config.mergeDateFields
          ? new Date(this.value.dateStart).getTime() !== new Date(this.value.dateEnd).getTime()
          : true;
    }

    const activeMenuItem = this.config.availableOptions
      .find(period => period.id === newData.type);
    if (activeMenuItem) {
      this.label = activeMenuItem.name;
    }

    if (emitValue) {
      this.hideModal();
      this.onChange.emit(newData);
    }
  }

  public formatDate(date: Date | string, format: string, subtract: number = 0): string {
    if (subtract && (this.label === this.staticTextContent.period ||
        this.label === this.staticTextContent.previousPeriod ||
        this.label === this.staticTextContent.currentPeriod)) {
      return date ? moment(date).subtract(subtract, 'd').format(format) : '';
    } else {
      return date ? moment(date).format(format) : '';
    }
  }

  public onInput(event: Event, valueType: 'dateStart' | 'dateEnd'): void {
    const inputValue = (event.target as HTMLInputElement).value;
    const dateValue = moment(inputValue, this.config.dateFormat.short_date_format);
    const valueIsValid = !this.config.required && inputValue === '' || dateValue.isValid();

    let userValue;
    if (dateValue.isValid()) {
      userValue = dateValue.toDate();
    } else if (valueIsValid) {
      userValue = null;
    } else {
      userValue = this.value[valueType];
    }


    this.value[valueType] = userValue || null;

    if (this.config.autoCorrectEndDate && this.value.dateStart > this.value.dateEnd) {
      this.value.dateEnd = new Date(this.value.dateStart);
      userValue = new Date(this.value.dateStart);
    }

    this.value.dateEnd = this.show2ndField ? this.value.dateEnd : (userValue || null);
    (event.target as HTMLInputElement).value = this.formatDate(userValue, this.config.dateFormat.short_date_format);

    if (valueIsValid) {
      const newValue = {
        type: this.config.required ? 'custom' : this.defaultValue.type,
        value: this.value,
      };
      this.defaultValue = newValue;
      this.onChange.emit(newValue);
    }
  }

  public isEditable(): boolean {
    return this.config.allowEdit || !!this.config.availableOptions
      .filter(option => option.id === 'custom')
      .length;
  }

  protected setLocale(locale: string): void {
    moment.locale(locale);
    const localeData: moment.Locale = moment.localeData();

    if (locale === 'en') {
      return;
    }

    const bsLocales = Object.values(locales);
    const particularLocale = bsLocales.find(b => b.abbr === locale);

    if (particularLocale) {
      defineLocale(particularLocale.abbr, particularLocale);
    }

    updateLocale(locale, this.normalizeDataForBootstrapLocale(localeData));
    this.bsLocaleService.use(locale);
  }
  /* should define the type as any here due to use the private property of the Locale object
  instead of redefining almost the same properties one by one */
  private normalizeDataForBootstrapLocale(localeData: any): LocaleData {
    return {
      ...localeData._config,
    };
  }
}
